// Canonical self-declared date-of-birth rule, shared by the server and the signup SPA so the
// two cannot disagree about what counts as a usable birthday. The server rejects a null result
// at signup (date_of_birth_required / invalid_date_of_birth); the SPA uses the same rule to keep
// the submit button disabled, so that rejection is never reached in the normal flow.
//
// A null DOB is unrecoverable for the user - nothing in the product prompts for it again - which
// is why signup must not persist one. See DEFAULT_AGE_BAND in the content-rating policy.

/** Reject implausible ages outright rather than banding them. */
export const MAX_AGE_YEARS = 120;

/**
 * Validate a self-declared DOB to a canonical `yyyy-mm-dd` string, or null when it is missing,
 * unparseable, in the future, or implausibly old. Never throws.
 *
 * `now` is injectable so callers (and tests) can pin the clock; it defaults to the current time.
 */
export function normaliseDateOfBirth(value: string | null | undefined, now: Date = new Date()): string | null {
    if (!value) return null;
    const dob = new Date(value);
    if (Number.isNaN(dob.getTime())) return null;
    if (dob.getTime() > now.getTime()) return null; // future date -> reject
    if (now.getFullYear() - dob.getFullYear() > MAX_AGE_YEARS) return null; // implausible -> reject
    // Canonical date-only form (drops any time component) for the ::date column.
    return dob.toISOString().slice(0, 10);
}

/** True when `value` is a DOB the signup endpoints will accept. */
export function isUsableDateOfBirth(value: string | null | undefined, now: Date = new Date()): boolean {
    return normaliseDateOfBirth(value, now) !== null;
}

/** A self-declared age in whole years (1..MAX_AGE_YEARS), or null. Accepts a number or digits only. */
export function parseAgeYears(value: unknown): number | null {
    const text = typeof value === "number" ? String(value) : typeof value === "string" ? value.trim() : "";
    if (!/^\d{1,3}$/.test(text)) return null;
    const years = Number(text);
    return years >= 1 && years <= MAX_AGE_YEARS ? years : null;
}

/**
 * The YOUNGEST birth date consistent with a declared age: exactly `years` ago today (they turned
 * that age today). Age bands derived from it therefore never place someone OLDER than they said.
 * It is an age-derived date, not a recorded birthday; Feb 29 clamps to Feb 28 in a common year.
 */
export function birthDateFromAge(years: number, now: Date = new Date()): string {
    const y = now.getUTCFullYear() - years, m = now.getUTCMonth();
    const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    return new Date(Date.UTC(y, m, Math.min(now.getUTCDate(), lastDay))).toISOString().slice(0, 10);
}
